/**
 * globalMessageClose 插件
 *
 * 功能：全局拦截 Vue.prototype.$message，当页面有 duration:0（不自动关闭）的
 *       消息堆积时，在第一条消息正上方注入"一键关闭"浮动按钮。
 *       点击按钮调用 $message.closeAll() 并重置计数。
 *
 * 使用：在 main.js 中 Vue.use(GlobalMessageClose) 即可，无需改动任何页面。
 */

import Vue from 'vue';

// ---------- 响应式共享状态 ----------
const state = Vue.observable({ count: 0, top: 20 });

// ---------- 浮动按钮组件 ----------
const CloseAllBtn = Vue.extend({
  name: 'CloseAllMessagesBtn',
  data() {
    return { state };
  },
  computed: {
    visible() {
      return this.state.count > 0;
    },
  },
  methods: {
    handleClose() {
      Vue.prototype.$message.closeAll();
      state.count = 0;
    },
  },
  render(h) {
    if (!this.visible) return h('div', { style: { display: 'none' } });
    return h(
      'div',
      {
        class: 'global-close-all-btn',
        style: { top: this.state.top + 'px' },
        on: { click: this.handleClose },
      },
      [
        h('i', { class: 'el-icon-circle-close' }),
        ' 一键关闭',
      ]
    );
  },
});

// ---------- 插件样式（注入一次） ----------
let styleInjected = false;
function injectStyle() {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
.global-close-all-btn {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffa39e;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(245,34,45,0.18);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  user-select: none;
  white-space: nowrap;
  animation: closeAllFadeIn 0.2s ease;
}
.global-close-all-btn:hover {
  background: #ff4d4f;
  color: #fff;
  border-color: #ff4d4f;
  box-shadow: 0 4px 14px rgba(245,34,45,0.32);
}
.global-close-all-btn .el-icon-circle-close {
  font-size: 14px;
}
@keyframes closeAllFadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
  `;
  document.head.appendChild(style);
}

// ---------- MutationObserver：跟踪第一条消息的位置 ----------
let observer = null;

function getFirstMessageTop() {
  const msgs = document.querySelectorAll('.el-message');
  if (!msgs.length) return null;
  const rect = msgs[0].getBoundingClientRect();
  // 按钮高度约 30px + 6px 间距，放在第一条消息正上方
  return Math.max(0, rect.top - 36);
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver(() => {
    const top = getFirstMessageTop();
    if (top !== null) {
      state.top = top;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// ---------- 挂载浮动按钮到 body ----------
let btnVm = null;
function mountBtn() {
  if (btnVm) return;
  const el = document.createElement('div');
  document.body.appendChild(el);
  btnVm = new CloseAllBtn().$mount(el);
  startObserver();
}

// ---------- 插件安装 ----------
const GlobalMessageClose = {
  install(Vue) {
    injectStyle();

    // 等 DOM 就绪后挂载按钮
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mountBtn);
    } else {
      mountBtn();
    }

    // 保存原始 $message
    const originalMessage = Vue.prototype.$message;

    // 代理函数：拦截所有 $message 调用
    function proxiedMessage(options) {
      if (typeof options === 'string') {
        return originalMessage(options);
      }

      const opts = { ...options };
      const originalOnClose = opts.onClose;

      // 仅对不自动关闭的消息（duration === 0）计入"需手动关闭"数量
      if (opts.duration === 0) {
        state.count++;
        opts.onClose = function () {
          state.count = Math.max(0, state.count - 1);
          if (typeof originalOnClose === 'function') originalOnClose();
        };
      }

      return originalMessage(opts);
    }

    // 复制静态方法（success / warning / error / info / closeAll）
    ;['success', 'warning', 'error', 'info'].forEach(type => {
      proxiedMessage[type] = function (options) {
        if (typeof options === 'string') {
          return originalMessage[type](options);
        }
        const opts = { ...options };
        const originalOnClose = opts.onClose;
        if (opts.duration === 0) {
          state.count++;
          opts.onClose = function () {
            state.count = Math.max(0, state.count - 1);
            if (typeof originalOnClose === 'function') originalOnClose();
          };
        }
        return originalMessage[type](opts);
      };
    });

    proxiedMessage.closeAll = function () {
      state.count = 0;
      return originalMessage.closeAll();
    };

    Vue.prototype.$message = proxiedMessage;
  },
};

export default GlobalMessageClose;
